import React, { useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { PaletteMode } from "@mui/material/styles";
import { createUniboxTheme } from "./theme";

function isEmbedded(): boolean {
  try {
    return new URLSearchParams(window.location.search).get("embedded") === "1";
  } catch {
    return false;
  }
}

function parseMode(value: unknown): PaletteMode | null {
  if (value === "dark" || value === "light") return value;
  return null;
}

function readStoredMode(): PaletteMode {
  const saved = localStorage.getItem("themeMode");
  return saved === "dark" ? "dark" : "light";
}

type Props = {
  children: React.ReactNode;
};

export default function UniboxThemeProvider({ children }: Props) {
  const embedded = useMemo(() => isEmbedded(), []);
  const [mode, setMode] = useState<PaletteMode>(() =>
    embedded ? "light" : readStoredMode()
  );

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    document.body.setAttribute("data-theme", mode);
    if (!embedded) {
      localStorage.setItem("themeMode", mode);
    }
  }, [mode, embedded]);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const data = e.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "CRM_AUTH_TOKEN") {
        const fromToken = parseMode(data.themeMode);
        if (fromToken) setMode(fromToken);
      }

      if (data.type === "CRM_THEME_MODE") {
        const fromTheme = parseMode(data.mode);
        if (fromTheme) setMode(fromTheme);
      }
    };

    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const theme = useMemo(() => createUniboxTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
